import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../features/auth/services/auth.service';
import { PaymentTransactionStatus } from './payment-api.service';

export interface PaymentNotification {
  orderId: string;
  orderCode: string;
  /**
   * 'UnderReview' là trường hợp tiền đã bị trừ nhưng giao dịch còn phải đối soát
   * (VNPay trả vnp_ResponseCode = 07 hoặc vnp_TransactionStatus = 04).
   */
  status: 'Success' | 'Failed' | 'UnderReview';
  transactionId?: string;
  amount?: number;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignalrService {
  private hubConnection: HubConnection | null = null;
  private paymentNotificationSubject = new BehaviorSubject<PaymentNotification | null>(null);
  public paymentNotification$ = this.paymentNotificationSubject.asObservable();

  private connectionPromise: Promise<void> | null = null;
  private connectionUrl = environment.signalRHubUrl || `${environment.apiURL}/api/paymentHub`;

  /**
   * Các group đang tham gia. SignalR reconnect lại bằng một connectionId mới, và server không nhớ
   * connection cũ thuộc group nào - không join lại thì mọi thông báo sau khi rớt mạng đều mất.
   */
  private joinedGroups = new Set<string>();

  constructor(private authService: AuthService) {}

  private async startConnectionInternal(): Promise<void> {
    const token = this.authService.getToken();

    if (!token) {
      return Promise.reject('No auth token');
    }

    if (this.authService.isTokenExpired(token)) {
      return Promise.reject('Token expired');
    }

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(this.connectionUrl, {
        accessTokenFactory: () => this.authService.getToken() ?? token,
        // Không ép cứng WebSockets: proxy hoặc firewall không nâng cấp được WebSocket sẽ làm hỏng
        // toàn bộ luồng thông báo thanh toán. Để SignalR tự hạ xuống SSE rồi long polling.
        transport:
          HttpTransportType.WebSockets |
          HttpTransportType.ServerSentEvents |
          HttpTransportType.LongPolling
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000])
      .configureLogging(LogLevel.Information)
      .build();

    this.hubConnection.on('PaymentCompleted', (notification: PaymentNotification) => {
      // Normalize payload just in case BE sends PascalCase properties
      const normalizedData: PaymentNotification = {
        orderId: (notification as any).orderId || (notification as any).OrderId,
        orderCode: (notification as any).orderCode || (notification as any).OrderCode,
        status: (notification as any).status || (notification as any).Status,
        transactionId: (notification as any).transactionId || (notification as any).TransactionId,
        amount: (notification as any).amount || (notification as any).Amount,
        message: (notification as any).message || (notification as any).Message,
      };
      this.paymentNotificationSubject.next(normalizedData);
    });

    this.hubConnection.on('JoinedPaymentGroup', (orderId: string) => {
      // Group join confirmed
    });

    this.hubConnection.onreconnected(async () => {
      // Connection mới thì group cũ không còn - vào lại tất cả các group đang theo dõi.
      for (const orderId of this.joinedGroups) {
        try {
          await this.hubConnection?.invoke('JoinPaymentGroup', orderId);
        } catch {
          // Màn hình thanh toán vẫn còn polling check-status làm lưới an toàn.
        }
      }
    });

    this.hubConnection.onclose(() => {
      this.hubConnection = null;
      this.connectionPromise = null;
    });

    try {
      await this.hubConnection.start();
    } catch (err) {
      this.hubConnection = null;
      this.connectionPromise = null;
      throw err;
    }
  }

  public ensureConnected(): Promise<void> {
    if (!this.connectionPromise) {
      this.connectionPromise = this.startConnectionInternal();
    }
    return this.connectionPromise;
  }

  public async joinGroup(orderId: string): Promise<void> {
    // Ghi nhận trước khi kết nối: nếu lần join này hỏng, lần reconnect sau vẫn vào lại được group.
    this.joinedGroups.add(orderId);

    await this.ensureConnected();

    if (this.hubConnection?.state !== HubConnectionState.Connected) {
      throw new Error('SignalR connection is not in the "Connected" state.');
    }

    await this.hubConnection.invoke('JoinPaymentGroup', orderId);
  }

  public async leaveGroup(orderId: string): Promise<void> {
    this.joinedGroups.delete(orderId);

    if (this.hubConnection?.state === HubConnectionState.Connected) {
      await this.hubConnection.invoke('LeavePaymentGroup', orderId);
    }
  }

  /** Xoá thông báo đã xử lý để lần vào màn hình sau không nhận lại kết quả cũ. */
  public clearNotification(): void {
    this.paymentNotificationSubject.next(null);
  }

  public async stopConnection(): Promise<void> {
    this.joinedGroups.clear();

    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.hubConnection = null;
      this.connectionPromise = null;
    }
  }
}
